/**
 * glTF loader chain with Draco, KTX2, and Meshopt decompression support.
 */

import type * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { Logger } from '../core/Logger.js';

export interface GLTFResult {
  scene: THREE.Group;
  scenes: THREE.Group[];
  cameras: THREE.Camera[];
  animations: THREE.AnimationClip[];
  asset: { version: string; generator?: string };
}

export class GLTFPipeline {
  private gltfLoader: GLTFLoader | null = null;
  private dracoLoader: DRACOLoader | null = null;
  private ktx2Loader: KTX2Loader | null = null;
  /** true when the KTX2 loader was lent by another pipeline (never disposed here) */
  private ktx2Shared = false;
  private initialized = false;

  /** The KTX2 transcoder this pipeline uses — lend it to a second pipeline instead of building another worker pool. */
  get ktx2(): KTX2Loader | null { return this.ktx2Loader; }

  /**
   * Initialize the loader chain. Must be called after renderer.init().
   */
  init(renderer: THREE.WebGPURenderer, sharedKtx2: KTX2Loader | null = null): void {
    if (this.initialized) return;

    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath('/draco/');

    // A SECOND KTX2Loader means a second transcoder worker pool — three warns
    // "Multiple active KTX2 loaders may cause performance issues" (seen when an
    // app built its own ModelPipeline beside App.gltf). Share one when lent.
    if (sharedKtx2) {
      this.ktx2Loader = sharedKtx2;
      this.ktx2Shared = true;
    } else {
    this.ktx2Loader = new KTX2Loader();
    this.ktx2Loader.setTranscoderPath('/basis/');
    this.ktx2Loader.detectSupport(renderer);
    // Transcode-target diagnostics + override (`?ktx=etc2|rgba|astc`):
    // structure textures render WHITE on Quest 3 (Adreno + the r185 WebGL
    // backend) while the identical GLB/loader/backend renders textured on
    // desktop — the delta is the transcode target (ASTC on-device vs BC on
    // desktop). The override lets a single on-device run bisect the broken
    // format; the log line reaches devtools-less headsets via the beacon.
    {
      const cfg = (this.ktx2Loader as unknown as { workerConfig: Record<string, boolean> }).workerConfig;
      const force = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('ktx') : null;
      if (force === 'etc2') { cfg.astcSupported = false; }
      else if (force === 'rgba') { for (const k of Object.keys(cfg)) cfg[k] = false; }
      else if (force === 'astc') { cfg.astcSupported = true; }
      const targets = Object.entries(cfg).filter(([, v]) => v).map(([k]) => k.replace('Supported', '')).join(',');
      Logger.info('Assets', `KTX2 transcode targets${force ? ` (forced ${force})` : ''}: ${targets || 'rgba-fallback'}`);
    }
    }

    // Meshopt decode on worker threads: without useWorkers every EXT_meshopt
    // buffer decodes synchronously in main-thread WASM at parse time — a
    // per-GLB stall during streaming (GLTFLoader routes through
    // decodeGltfBufferAsync, which dispatches to workers once enabled).
    // Sized to the machine (LOD-switch research S6, 2026-09-02): cores − 2,
    // clamped 1..4, for BOTH decoders — the KTX2 transcoder pool shares the
    // same budget so a level-swap wave never queues behind a single worker.
    const workers = Math.max(1, Math.min(4, (typeof navigator !== 'undefined' && navigator.hardwareConcurrency ? navigator.hardwareConcurrency : 4) - 2));
    if (typeof Worker !== 'undefined') {
      try { MeshoptDecoder.useWorkers(workers); } catch { /* workers unavailable — sync decode fallback */ }
      try { this.ktx2Loader.setWorkerLimit(workers); } catch { /* older loader */ }
    }

    this.gltfLoader = new GLTFLoader();
    this.gltfLoader.setDRACOLoader(this.dracoLoader);
    this.gltfLoader.setKTX2Loader(this.ktx2Loader);
    this.gltfLoader.setMeshoptDecoder(MeshoptDecoder);

    this.initialized = true;
    Logger.info('Assets', 'glTF pipeline initialized (Draco + KTX2 + Meshopt)');
  }

  /**
   * Load a glTF/GLB file.
   */
  async load(
    url: string,
    onProgress?: (event: ProgressEvent) => void,
  ): Promise<GLTFResult> {
    if (!this.gltfLoader) {
      throw new Error('GLTFPipeline not initialized. Call init() first.');
    }

    return new Promise((resolve, reject) => {
      this.gltfLoader!.load(
        url,
        (gltf) => {
          Logger.debug('Assets', `Loaded: ${url}`);
          resolve({
            scene: gltf.scene,
            scenes: gltf.scenes,
            cameras: gltf.cameras,
            animations: gltf.animations,
            asset: gltf.asset as { version: string; generator?: string },
          });
        },
        onProgress,
        (error) => {
          Logger.error('Assets', `Failed to load: ${url}`, error);
          reject(error);
        },
      );
    });
  }

  dispose(): void {
    this.dracoLoader?.dispose();
    if (!this.ktx2Shared) this.ktx2Loader?.dispose();
    this.ktx2Loader = null;
    this.ktx2Shared = false; // else the next init() without a lent loader would never dispose its own
    this.gltfLoader = null;
    this.initialized = false;
  }
}
